import type { IAnimation } from "./util/Animation";
import type { Entity } from "./util/Entity";
import type { Sprite } from "./render/Sprite";
import type { Theme } from "./Theme";
import type { IPoint } from "./util/IPoint";
import type { IRenderer, IRendererClass, IPointerEvent } from "./render/backend/Renderer";
import type { Timezone } from "./util/Timezone";

import { AnimationState } from "./util/Animation";
import { Container } from "./render/Container";
import { Text } from "./render/Text";
import { HorizontalLayout } from "./render/HorizontalLayout";
import { VerticalLayout } from "./render/VerticalLayout";
import { GridLayout } from "./render/GridLayout";
import { IDisposer, Disposer } from "./util/Disposer";
import { ResizeSensor } from "./util/ResizeSensor";
import { InterfaceColors, IInterfaceColorsSettings } from "./util/InterfaceColors";
import { Graphics } from "./render/Graphics";
import { Rectangle } from "./render/Rectangle";
import { Tooltip } from "./render/Tooltip";
import { NumberFormatter } from "./util/NumberFormatter";
import { DateFormatter } from "./util/DateFormatter";
import { DurationFormatter } from "./util/DurationFormatter";
import { ILocale, Language } from "./util/Language";
import { Events, EventDispatcher } from "./util/EventDispatcher";
import { DefaultTheme } from "../themes/DefaultTheme";
import { CanvasRenderer } from "./render/backend/CanvasRenderer";
import { p100, percent, isPercent, Percent } from "./util/Percent";
import { Color, color } from "./util/Color";
import { populateString } from "./util/PopulateString";
import { registry } from "./Registry";

import * as $order from "./util/Order";
import * as $array from "./util/Array";
import * as $object from "./util/Object";
import * as $utils from "./util/Utils";
import * as $type from "./util/Type";
import * as $ease from "./util/Ease";


import en from "../../locales/en";


function rAF(fps: number | undefined, callback: (currentTime: number) => void): void {
	if (fps == null) {
		requestAnimationFrame(callback);

	} else {
		setTimeout(() => {
			requestAnimationFrame(callback);
		}, 1000 / fps);
	}
}


/**
 * @ignore
 */
interface IParent extends Entity {
	_prepareChildren(): void;
	_updateChildren(): void;
	_childrenUpdt: boolean;
	_childrenPrep: boolean;
}

interface IBounds extends Entity {
	depth(): number;
	_updateBounds(): void;
}


export interface ISize {
	width: number,
	height: number,
}


export interface IRootEvents {
	/**
	 * A frame started rendering. `timestamp` is the frame's time in milliseconds.
	 */
	framestarted: {
		timestamp: number;
	};
	/**
	 * A frame finished rendering. `timestamp` is the frame's time in milliseconds.
	 */
	frameended: {
		timestamp: number;
	};
}


export interface IRootSettings {

	/**
	 * Draws the chart at a resolution of `1` on iOS and iPadOS devices instead
	 * of the screen's pixel ratio, to stay within their memory limits. Set to
	 * `false` for sharper charts there.
	 *
	 * @default true
	 */
	useSafeResolution?: boolean;

	/**
	 * Extra space in pixels on each side of the chart where tooltips can still
	 * be drawn, letting them go outside the chart's container.
	 *
	 * @since 5.2.24
	 */
	tooltipContainerBounds?: { top: number, left: number, right: number, bottom: number };

	/**
	 * Set to `false` to disable all accessibility features.
	 *
	 * NOTE: once disabled, accessibility cannot be re-enabled on a live `Root` object.
	 *
	 * @default true
	 * @since 5.3.0
	 */
	accessible?: boolean;

	/**
	 * If set to `true`, the parent inner `<div>` element will become a focusable
	 * element.
	 *
	 * @since 5.3.17
	 * @default false
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/accessibility/#Accessibility_of_Root_element} for more info
	 */
	focusable?: boolean;

	/**
	 * Distance between focused element and its highlight square in pixels.
	 *
	 * @since 5.6.0
	 * @default 2
	 */
	focusPadding?: number;

	/**
	 * If set to some string, it will be used as inner `<div>` ARIA-LABEL.
	 *
	 * Should be used in conjunction with `focusable`.
	 *
	 * @since 5.3.17
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/accessibility/#Accessibility_of_Root_element} for more info
	 */
	ariaLabel?: string;

	/**
	 * Allows setting a "role" for the inner `<div>`.
	 *
	 * @since 5.3.17
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/accessibility/#Accessibility_of_Root_element} for more info
	 */
	role?: string;

	/**
	 * A function that returns the width and height in pixels to draw the chart
	 * at, given the container's measured box. It runs when the chart is created
	 * and on every resize.
	 */
	calculateSize?: (dimensions: DOMRect) => ISize;

	/**
	 * Cleans potentially malicious code out of HTML content (`html` and
	 * `labelHTML` settings, HTML tooltips, modals, export menu labels) before
	 * it goes on the page.
	 *
	 * Turn it off only if you trust all HTML content: unsanitized HTML from
	 * untrusted data can lead to XSS attacks.
	 *
	 * @default true
	 * @since 5.19.0
	 */
	sanitizeHTML?: boolean;

	/**
	 * Font family for all labels. It overrides the theme's, but not a
	 * `fontFamily` set on a label itself.
	 *
	 * @since 5.20.2
	 */
	fontFamily?: string;

	/**
	 * Font size for all labels: a number in pixels, or a CSS size such as
	 * `"1.2em"`. It overrides the theme's, but not a `fontSize` set on a label
	 * itself.
	 *
	 * @since 5.20.2
	 */
	fontSize?: number | string;

	/**
	 * Font weight for all labels. It overrides the theme's, but not a
	 * `fontWeight` set on a label itself.
	 *
	 * @since 5.20.2
	 */
	fontWeight?: "normal" | "bold" | "bolder" | "lighter" | "100" | "200" | "300" | "400" | "500" | "600" | "700" | "800" | "900";

	/**
	 * The renderer that draws the chart. Defaults to `Root.defaultRenderer`,
	 * which is `CanvasRenderer` unless changed.
	 *
	 * To draw with SVG, use `am5.SVGRenderer`:
	 *
	 * ```TypeScript
	 * const root = am5.Root.new("chartdiv", {
	 *   renderer: am5.SVGRenderer
	 * });
	 * ```
	 * ```JavaScript
	 * var root = am5.Root.new("chartdiv", {
	 *   renderer: am5.SVGRenderer
	 * });
	 * ```
	 *
	 * Can only be set when the Root is created.
	 *
	 * @since 5.21.0
	 */
	renderer?: IRendererClass;

	/**
	 * Makes the chart's interface colors (labels, grid, axes, buttons) follow
	 * the system colors when the page is in a forced colors mode, such as
	 * Windows High Contrast, so they stay visible. Series colors are kept.
	 *
	 * @default true
	 * @since 5.21.0
	 */
	forcedColors?: boolean;
}

// Interface colors that follow the system colors in a forced colors mode
const FORCED_COLORS: { [K in keyof IInterfaceColorsSettings]?: string } = {
	text: "CanvasText",
	grid: "CanvasText",
	alternativeBackground: "CanvasText",
	stroke: "CanvasText",
	background: "Canvas",
	alternativeText: "Canvas",
	fill: "Canvas",
	// Hover and active stay ButtonFace: ButtonText on Highlight can be unreadable
	primaryButton: "ButtonFace",
	primaryButtonHover: "ButtonFace",
	primaryButtonDown: "ButtonFace",
	primaryButtonActive: "ButtonFace",
	primaryButtonDisabled: "ButtonFace",
	secondaryButton: "ButtonFace",
	secondaryButtonHover: "ButtonFace",
	secondaryButtonDown: "ButtonFace",
	secondaryButtonActive: "ButtonFace",
	primaryButtonText: "ButtonText",
	primaryButtonStroke: "ButtonText",
	secondaryButtonText: "ButtonText",
	secondaryButtonStroke: "ButtonText",
	primaryButtonTextDisabled: "GrayText",
	disabled: "GrayText"
};

// One media query and one color lookup for all charts on the page
let forcedColorsQuery: MediaQueryList | null | undefined;
let systemColors: { [name: string]: Color } | undefined;

function getForcedColorsQuery(): MediaQueryList | null {
	if (forcedColorsQuery === undefined) {
		forcedColorsQuery = window.matchMedia ? window.matchMedia("(forced-colors: active)") : null;
	}
	return forcedColorsQuery;
}

// Resolves system color keywords (CanvasText, ...) to colors
function getSystemColors(): { [name: string]: Color } {
	if (!systemColors) {
		const colors: { [name: string]: Color } = {};
		const probe = document.createElement("div");
		// Not forced itself, so it reports the system color asked for
		probe.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;forced-color-adjust:none";
		document.body.appendChild(probe);
		$array.each(["Canvas", "CanvasText", "ButtonFace", "ButtonText", "GrayText"], (name) => {
			probe.style.color = name;
			colors[name] = Color.fromCSS(getComputedStyle(probe).color);
		});
		document.body.removeChild(probe);
		systemColors = colors;
	}
	return systemColors;
}


// TODO implement Disposer
/**
 * The root of a chart: it ties the chart to an HTML element and holds its
 * global settings, formatters, locale and themes.
 *
 * @see {@link https://www.amcharts.com/docs/v5/getting-started/#Root_element} for more info
 */
export class Root implements IDisposer {

	/**
	 * The HTML element the chart is in.
	 */
	public dom: HTMLElement;
	public _inner: HTMLElement;

	protected _settings: IRootSettings;

	/**
	 * Settings the Root filled in for itself, so that serializing can leave them
	 * out - a `Root` is not an `Entity`, so it has no `_computedProperties`.
	 *
	 * @ignore
	 */
	public _computedSettings: { [K in keyof IRootSettings]?: boolean } = {};
	protected _isDirty: boolean = false;
	protected _isDirtyParents: boolean = false;
	protected _isDirtyAnimation: boolean = false;
	protected _dirty: { [id: number]: Entity } = {};
	protected _dirtyParents: { [id: number]: IParent } = {};
	protected _dirtyBounds: { [id: number]: IBounds } = {};
	protected _dirtyPositions: { [id: number]: Sprite } = {};

	protected _ticker: ((currentTime: number) => void) | null = null;
	protected _tickers: Array<(currentTime: number) => void> = [];

	protected _updateTick: boolean = true;
	public skipRenderFrame: boolean = false;

	/**
	 * Root's event dispatcher.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/events/} for more info
	 */
	public events: EventDispatcher<Events<this, IRootEvents>> = new EventDispatcher();

	/**
	 * Time in milliseconds of the frame being rendered, or `null` while no
	 * frames run.
	 *
	 * @ignore
	 */
	public animationTime: number | null = null;

	private _animations: Array<IAnimation> = [];

	public _renderer: IRenderer;

	public _rootContainer!: Container;

	/**
	 * The main container: charts and other elements go into its `children`.
	 */
	public container!: Container;

	/**
	 * The [[Container]] tooltips are drawn in, above the chart.
	 */
	public tooltipContainer!: Container
	protected _tooltipContainerSettings!: { top: number, left: number, right: number, bottom: number };

	public _tooltip!: Tooltip;

	// Locale-related

	/**
	 * @ignore
	 */
	public language: Language = Language.new(this, {});

	/**
	 * Locale for the chart's texts and formatting. English by default.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/locales/}
	 */
	public locale: ILocale = en;

	// Date-time related

	/**
	 * Treats dates as UTC instead of the user's local time, when formatting
	 * and parsing them and when placing date axis grid. It takes precedence
	 * over `timezone`.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/formatters/formatting-dates/#utc-and-time-zones} for more info
	 */
	public utc: boolean = false;

	/**
	 * Time zone to show dates in, instead of the user's local one. Set it to a
	 * [[Timezone]] with an IANA name, e.g.
	 * `am5.Timezone.new("America/Vancouver")`. For UTC, `utc` is faster.
	 *
	 * Every date is converted, which can slow down charts with large data sets.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/getting-started/root-element/#time-zone} for more info
	 * @since 5.1.0
	 */
	public timezone?: Timezone;

	/**
	 * Maximum frames per second. When not set, the chart renders on every
	 * animation frame of the browser.
	 *
	 * Each frame waits `1000 / fps` milliseconds after the previous one is
	 * drawn, then for the screen's next refresh, so the rate achieved is lower:
	 * `fps: 30` gives about 20 on a 60 Hz screen.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/getting-started/root-element/#Performance} for more info
	 */
	public fps: number | undefined;

	/**
	 * Number formatter.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/formatters/formatting-numbers/} for more info
	 */
	public numberFormatter: NumberFormatter = NumberFormatter.new(this, {});

	/**
	 * Date/time formatter.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/formatters/formatting-dates/} for more info
	 */
	public dateFormatter: DateFormatter = DateFormatter.new(this, {});

	/**
	 * Duration formatter.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/formatters/formatting-dates/} for more info
	 */
	public durationFormatter: DurationFormatter = DurationFormatter.new(this, {});


	// Accessibility

	/**
	 * Tab index for the whole chart.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/accessibility/} for more info
	 */
	public tabindex: number = 0;

	//@todo maybe make this better
	protected _tabindexes: Sprite[] = [];

	protected _a11yD: boolean = false;
	protected _focusElementDirty: boolean = false;
	protected _focusElementContainer: HTMLDivElement | undefined;
	protected _focusedSprite: Sprite | undefined;
	protected _isShift: boolean | undefined;
	protected _keyboardDragPoint: IPoint | undefined;
	protected _tooltipElementContainer: HTMLDivElement | undefined;
	protected _readerAlertElement: HTMLDivElement | undefined;

	// Each `focusableGroup` is one TAB stop: the member that is it, and groups
	// to re-check before the next frame
	protected _groupStops: { [group: string]: Sprite } = {};
	protected _dirtyGroups: { [group: string]: string | number } = {};

	public _logo?: Container;

	public _tooltipDiv: HTMLDivElement | undefined;

	/**
	 * Nonce for the `<style>` elements the chart adds to the page, for pages
	 * with a strict Content Security Policy.
	 */
	public nonce?: string;

	/**
	 * Colors of the chart's interface: text, grid, buttons and other controls.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/colors-gradients-and-patterns/#Interface_colors} for more info
	 */
	public interfaceColors: InterfaceColors;

	/**
	 * A shared [[VerticalLayout]] for a [[Container]]'s `layout` setting.
	 *
	 * @default VerticalLayout.new()
	 */
	public verticalLayout: VerticalLayout = VerticalLayout.new(this, {});

	/**
	 * A shared [[HorizontalLayout]] for a [[Container]]'s `layout` setting.
	 *
	 * @default HorizontalLayout.new()
	 */
	public horizontalLayout: HorizontalLayout = HorizontalLayout.new(this, {});

	/**
	 * A shared [[GridLayout]] for a [[Container]]'s `layout` setting.
	 *
	 * @default GridLayout.new()
	 */
	public gridLayout: GridLayout = GridLayout.new(this, {});

	public _paused: boolean = false;

	/**
	 * Resizes the chart automatically when its container's size changes. With
	 * `false`, call `resize()` to resize it.
	 */
	public autoResize: boolean = true;

	protected _fontHash: string = "";
	protected _styles?: CSSStyleDeclaration;

	protected _isDisposed: boolean = false;
	protected _disposers: Array<IDisposer> = [];

	protected _resizeSensorDisposer?: IDisposer;

	public _tooltips: Array<Tooltip> = [];

	protected _htmlElementContainer: HTMLDivElement | undefined;
	protected _htmlEnabledContainers: Container[] = [];

	/**
	 * Objects of this root that have an `id` setting, by that `id`.
	 *
	 * @since 5.11.0
	 */
	public entitiesById: { [index: string]: any } = {};

	protected _systemTooltip!: Tooltip;

	protected constructor(id: string | HTMLElement, settings: IRootSettings = {}, isReal: boolean) {

		if (!isReal) {
			throw new Error("You cannot use `new Class()`, instead use `Class.new()`");
		}

		this._settings = settings;

		if (settings.accessible == false) {
			this._a11yD = true;
		}

		if (settings.useSafeResolution == null) {
			settings.useSafeResolution = true;
			this._computedSettings.useSafeResolution = true;
		}

		if (settings.sanitizeHTML == null) {
			settings.sanitizeHTML = true;
			this._computedSettings.sanitizeHTML = true;
		}

		let resolution;

		if (settings.useSafeResolution) {
			resolution = $utils.getSafeResolution();
		}

		const RendererClass = settings.renderer || Root.defaultRenderer;
		this._renderer = new RendererClass(resolution);

		const dom = Root._getDOM(id);

		this.interfaceColors = InterfaceColors.new(this, {});

		this.dom = dom;

		let inner: HTMLDivElement = document.createElement("div");
		inner.style.position = "relative";
		inner.style.width = "100%";
		inner.style.height = "100%";
		dom.appendChild(inner);

		const tooltipContainerBounds = settings.tooltipContainerBounds;
		if (tooltipContainerBounds) {
			this._tooltipContainerSettings = tooltipContainerBounds;
		}

		this._inner = inner;

		this._updateComputedStyles();

		registry.rootElements.push(this);
	}


	/**
	 * Renderer for Roots that do not set `renderer`.
	 *
	 * @default CanvasRenderer
	 * @since 5.21.0
	 */
	public static defaultRenderer: IRendererClass = CanvasRenderer;

	/**
	 * Creates a Root in an HTML element. An element can hold only one Root:
	 * creating another one there throws an error, unless
	 * `am5.registry.autoDispose` is `true`.
	 *
	 * @param   id        The element, or its `id`
	 * @param   settings  Settings
	 * @return            Root
	 */
	public static new(id: string | HTMLElement, settings?: IRootSettings): Root {
		// Before the constructor: its fields already start the ticker
		const dom = Root._getDOM(id);

		$array.each(registry.rootElements, (root) => {
			if (root.dom === dom) {
				if (registry.autoDispose) {
					root.dispose();
				}
				else {
					throw new Error("You cannot have multiple Roots on the same DOM node");
				}
			}
		});

		const root = new Root(id, settings, true);
		root._init();
		return root;
	}

	protected static _getDOM(id: string | HTMLElement): HTMLElement {
		const dom = id instanceof HTMLElement ? id : document.getElementById(id);
		if (dom === null) {
			throw new Error("Could not find HTML element with id `" + id + "`");
		}
		return dom;
	}

	/**
	 * Moves the chart to another HTML element and resizes it to fit there.
	 * Nothing happens if no element with that `id` exists.
	 *
	 * @param  id  The element, or its `id`
	 */
	public moveDOM(id: string | HTMLElement): void {
		let dom: HTMLElement | null;

		if (id instanceof HTMLElement) {
			dom = id;
		}
		else {
			dom = document.getElementById(id);
		}

		if (dom) {
			while (this.dom.childNodes.length > 0) {
				dom.appendChild(this.dom.childNodes[0]);
			}
			this.dom = dom;
			// The cached computed style belongs to the old element
			this._styles = undefined;
			this._initResizeSensor();
			this.resize();
		}

	}


	protected _handleLogo(): void {
		if (this._logo) {
			const w = this.dom.offsetWidth;
			const h = this.dom.offsetHeight;
			if ((w <= 150) || (h <= 60)) {
				this._logo.hide();
			}
			else {
				this._logo.show();
			}
		}
	}

	public _showBranding(): void {
		if (!this._logo) {
			const logo = this.tooltipContainer.children.push(Container.new(this, {
				interactive: true,
				interactiveChildren: false,
				position: "absolute",
				setStateOnChildren: true,
				paddingTop: 9,
				paddingRight: 9,
				paddingBottom: 9,
				paddingLeft: 9,
				scale: .6,
				y: percent(100),
				centerY: p100,
				tooltipY: 10,
				tooltipText: "Created using amCharts 5",
				tooltipX: p100,
				cursorOverStyle: "pointer",
				background: Rectangle.new(this, {
					fill: color(0x474758),
					fillOpacity: 0,
					tooltipY: 5
				})
			}));


			logo.set("tooltip", this.systemTooltip);

			logo.events.on("click", () => {
				window.open("https://www.amcharts.com/", "_blank");
			});

			logo.states.create("hover", {});

			const m = logo.children.push(Graphics.new(this, {
				stroke: color(0xcccccc),
				strokeWidth: 3,
				svgPath: "M5 25 L13 25h13.6c3.4 0 6 0 10.3-4.3s5.2-12 8.6-12c3.4 0 4.3 8.6 7.7 8.6M83.4 25H79.8c-3.4 0-6 0-10.3-4.3s-5.2-12-8.6-12-4.3 8.6-7.7 8.6",
				dx: -50,
				strokeOpacity: 0
			}));

			m.states._createC("hover", { stroke: color(0x3CABFF) });

			const a = logo.children.push(Graphics.new(this, {
				stroke: color(0x888888),
				strokeWidth: 3,
				svgPath: "M83.4 25h-31C37 25 39.5 4.4 28.4 4.4S18.9 24.2 4.3 25H0",
				dx: 50,
				strokeOpacity: 0
			}));

			const easing = $ease.out($ease.cubic);
			const duration = 1500;

			logo.setTimeout(() => {
				a.animate({
					key: "strokeOpacity",
					to: 1,
					duration: duration,
					easing: easing
				});

				a.animate({
					key: "dx",
					to: 0,
					duration: duration,
					easing: easing
				});

				m.animate({
					key: "dx",
					to: 0,
					duration: duration,
					easing: easing
				});

				m.animate({
					key: "strokeOpacity",
					to: 1,
					duration: duration,
					easing: easing
				});
			}, 3000);

			a.states.create("hover", { stroke: color(0x474758) });

			this._logo = logo;

			this._handleLogo();

		}
	}

	protected _getRealSize(): DOMRect {
		return this.dom.getBoundingClientRect();
	}

	protected _getCalculatedSize(rect: DOMRect): ISize {
		if (this._settings.calculateSize) {
			return this._settings.calculateSize(rect);

		} else {
			// Drawn at the size before CSS transforms, which the browser then applies
			const dom = this.dom;
			const width = dom.offsetWidth;
			const height = dom.offsetHeight;
			if (Math.abs(width - rect.width) > 1 || Math.abs(height - rect.height) > 1) {
				return { width, height };
			}
			return {
				width: rect.width,
				height: rect.height,
			};
		}
	}

	protected _init(): void {

		const settings = this._settings;
		if (settings.accessible !== false) {
			if (settings.focusable) {
				this._inner.setAttribute("focusable", "true");
				this._inner.setAttribute("tabindex", this.tabindex + "");
			}

			if (settings.ariaLabel) {
				this._inner.setAttribute("aria-label", settings.ariaLabel);
			}

			if (settings.role) {
				this._inner.setAttribute("role", settings.role);
			}
		}

		const renderer = this._renderer;

		const rect = this._getRealSize();
		const size = this._getCalculatedSize(rect);

		const width = Math.floor(size.width);
		const height = Math.floor(size.height);

		const realWidth = Math.floor(rect.width);
		const realHeight = Math.floor(rect.height);

		const rootContainer = Container.new(this, {
			visible: true,
			width: width,
			height: height,
		});
		this._rootContainer = rootContainer;
		this._rootContainer._defaultThemes.push(DefaultTheme.new(this));

		// the size is needed to rebuild this from a config, `visible` is the default
		const container = rootContainer.children.push(Container.new(this, { width: p100, height: p100 }));
		container._setC("visible", true);
		this.container = container;

		renderer.resize(realWidth, realHeight, width, height);

		//@todo: better appendChild - refer
		this._inner.appendChild(renderer.view);

		// TODO: TMP TMP TMP for testing only, remove
		//renderer.debugGhostView = true;

		this._initResizeSensor();

		// Web fonts that finish loading after text was measured
		const fonts = (<any>document).fonts;
		if (fonts && fonts.addEventListener) {
			const loadedFonts = () => {
				let count = 0;
				fonts.forEach((font: any) => {
					if (font.status == "loaded") {
						count++;
					}
				});
				return count;
			};

			// Only when a font did load: Firefox also fires loadingdone without one
			let loaded = loadedFonts();
			const remeasure = () => {
				const count = loadedFonts();
				if (count > loaded && !this._isDisposed) {
					this._invalidateLabelBounds(this._rootContainer, true);
					$array.each(this._tooltips, (tooltip) => {
						this._invalidateLabelBounds(tooltip, true);
					});
				}
				loaded = count;
			};
			this._disposers.push($utils.addEventListener(fonts, "loadingdone", remeasure));
			// WebKit fires no loadingdone
			this._disposers.push($utils.addEventListener(fonts, "loading", () => {
				fonts.ready.then(remeasure);
			}));
			if (fonts.status == "loading") {
				fonts.ready.then(remeasure);
			}
		}

		// HTML content holder
		const htmlElementContainer = document.createElement("div");
		this._htmlElementContainer = htmlElementContainer;
		htmlElementContainer.className = "am5-html-container";
		htmlElementContainer.style.position = "absolute";
		htmlElementContainer.style.pointerEvents = "none";
		if (!this._tooltipContainerSettings) {
			htmlElementContainer.style.overflow = "hidden";
		}
		this._inner.appendChild(htmlElementContainer);

		if (this._a11yD !== true) {

			// Create element which is used to make announcements to screen reader
			const readerAlertElement = document.createElement("div");
			readerAlertElement.className = "am5-reader-container";
			readerAlertElement.setAttribute("role", "alert");
			// readerAlertElement.style.zIndex = "-100000";
			// readerAlertElement.style.opacity = "0";
			// readerAlertElement.style.top = "0";
			readerAlertElement.style.position = "absolute";
			readerAlertElement.style.width = "1px";
			readerAlertElement.style.height = "1px";
			readerAlertElement.style.overflow = "hidden";
			readerAlertElement.style.clip = "rect(1px, 1px, 1px, 1px)";
			this._readerAlertElement = readerAlertElement;
			this._inner.appendChild(this._readerAlertElement);

			const focusElementContainer = document.createElement("div");
			focusElementContainer.className = "am5-focus-container";
			focusElementContainer.style.position = "absolute";
			focusElementContainer.style.pointerEvents = "none";
			focusElementContainer.style.top = "0px";
			focusElementContainer.style.left = "0px";
			focusElementContainer.style.overflow = "hidden";
			focusElementContainer.style.width = width + "px";
			focusElementContainer.style.height = height + "px";

			focusElementContainer.setAttribute("role", "graphics-document");
			if (settings.ariaLabel) {
				focusElementContainer.setAttribute("aria-label", settings.ariaLabel);
			}

			$utils.setInteractive(focusElementContainer, false);
			this._focusElementContainer = focusElementContainer;
			this._inner.appendChild(this._focusElementContainer);

			const tooltipElementContainer = document.createElement("div");
			this._tooltipElementContainer = tooltipElementContainer;
			tooltipElementContainer.className = "am5-tooltip-container";
			this._inner.appendChild(tooltipElementContainer);

			// Add keyboard events for accessibility, e.g. simulating drag with arrow
			// keys and click with ENTER
			if ($utils.supports("keyboardevents")) {

				this._disposers.push($utils.addEventListener(window, "keydown", (ev: KeyboardEvent) => {
					const eventKey = $utils.getEventKey(ev);
					if (eventKey == "Shift") {
						this._isShift = true;
					}
					else if (eventKey == "Tab") {
						this._isShift = ev.shiftKey;
					}
				}));

				this._disposers.push($utils.addEventListener(window, "keyup", (ev: KeyboardEvent) => {
					const eventKey = $utils.getEventKey(ev);
					if (eventKey == "Shift") {
						this._isShift = false;
					}
				}));

				this._disposers.push($utils.addEventListener(focusElementContainer, "click", () => {
					// Some screen readers convert ENTER (and some SPACE) press whil on
					// focused element to a "click" event, preventing actual "keydown"
					// event from firing. We're using this "click" event to still
					// generate internal click events.
					const focusedSprite = this._focusedSprite;
					if (focusedSprite) {
						const announceText = focusedSprite.get("clickAnnounceText", "");
						if (announceText !== "") {
							this.readerAlert(announceText);
						}
						const downEvent = renderer.getEvent(new MouseEvent("click"));
						focusedSprite.events.dispatch("click", {
							type: "click",
							originalEvent: downEvent.event,
							point: downEvent.point,
							simulated: true,
							target: focusedSprite
						});
					}
				}));

				this._disposers.push($utils.addEventListener(focusElementContainer, "keydown", (ev: KeyboardEvent) => {
					const focusedSprite = this._focusedSprite;
					if (focusedSprite) {
						if (ev.key == "Escape") {
							// ESC dismisses what focus brought up (a tooltip, a keyboard
							// drag) but keeps focus where it is
							if (focusedSprite.isDragging()) {
								this._stopKeyboardDrag(focusedSprite);
							}
							focusedSprite.hideTooltip();
							return;
						}
						let dragOffsetX = 0;
						let dragOffsetY = 0;
						// TODO: figure out if using bogus MouseEvent is fine, or it will
						// fail on some platforms
						const eventKey = $utils.getEventKey(ev);
						switch (eventKey) {
							case "Enter":
							case " ":
								// SPACE clicks what a browser would click with it: buttons,
								// checkboxes and the like
								if (eventKey == " " && ["button", "checkbox", "switch", "radio", "tab", "option", "menuitem", "menuitemcheckbox", "menuitemradio"].indexOf(focusedSprite.get("role", "")) === -1) {
									return;
								}
								const announceText = focusedSprite.get("clickAnnounceText", "");
								if (announceText !== "") {
									this.readerAlert(announceText);
								}
								ev.preventDefault();
								const downEvent = renderer.getEvent(new MouseEvent("mouse"));
								focusedSprite.events.dispatch("click", {
									type: "click",
									originalEvent: downEvent.event,
									point: downEvent.point,
									simulated: true,
									target: focusedSprite
								});
								return;
							case "ArrowLeft":
								dragOffsetX = -6;
								break;
							case "ArrowRight":
								dragOffsetX = 6;
								break;
							case "ArrowUp":
								dragOffsetY = -6;
								break;
							case "ArrowDown":
								dragOffsetY = 6;
								break;
							default:
								return;
						}

						if (dragOffsetX != 0 || dragOffsetY != 0) {
							ev.preventDefault();

							// In a focusable group arrow keys move between its members (on
							// key up); SHIFT + arrows drag a draggable member
							if (focusedSprite.get("focusableGroup") && !ev.shiftKey && !focusedSprite.isDragging()) {
								return;
							}

							if (!focusedSprite.isDragging()) {
								// Start dragging
								this._keyboardDragPoint = {
									x: 0,
									y: 0
								}

								const downEvent = renderer.getEvent(new MouseEvent("mousedown", {
									clientX: 0,
									clientY: 0
								}));

								downEvent.point = {
									x: 0,
									y: 0
								};

								if (focusedSprite.events.isEnabled("pointerdown")) {
									focusedSprite.events.dispatch("pointerdown", {
										type: "pointerdown",
										originalEvent: downEvent.event,
										point: downEvent.point,
										simulated: true,
										target: focusedSprite
									});
								}

							}
							else {
								// Move focus marker
								//this._positionFocusElement(focusedSprite);
							}

							// Move incrementally
							const dragPoint = this._keyboardDragPoint!;
							dragPoint.x += dragOffsetX;
							dragPoint.y += dragOffsetY;
							const moveEvent = renderer.getEvent(new MouseEvent("mousemove", {
								clientX: dragPoint.x,
								clientY: dragPoint.y
							}), false);

							if (focusedSprite.events.isEnabled("globalpointermove")) {
								focusedSprite.events.dispatch("globalpointermove", {
									type: "globalpointermove",
									originalEvent: moveEvent.event,
									point: moveEvent.point,
									simulated: true,
									target: focusedSprite
								});
							}
						}
					}
				}));

				this._disposers.push($utils.addEventListener(focusElementContainer, "keyup", (ev: KeyboardEvent) => {
					if (this._focusedSprite) {
						const focusedSprite = this._focusedSprite;
						const eventKey = $utils.getEventKey(ev);
						switch (eventKey) {
							case "ArrowLeft":
							case "ArrowRight":
							case "ArrowUp":
							case "ArrowDown":
								if (focusedSprite.isDragging()) {
									this._stopKeyboardDrag(focusedSprite);
									return;
								}
								else if (focusedSprite.get("focusableGroup")) {
									// Find next item in focusable group
									const group = focusedSprite.get("focusableGroup");
									const items = this._tabindexes.filter((item) => {
										return item === focusedSprite || (item.get("focusableGroup") == group && item.isVisibleDeep() && this._canTab(item));
									});
									let index = items.indexOf(focusedSprite);
									const lastIndex = items.length - 1;
									index += (eventKey == "ArrowRight" || eventKey == "ArrowDown") ? 1 : -1;
									if (index < 0) {
										index = lastIndex;
									}
									else if (index > lastIndex) {
										index = 0;
									}
									$utils.focus(items[index].getPrivate("focusElement")!.dom);
								}
								break;
						}
					}
				}));
			}
		}

		this._startTicker();
		this.setThemes([]);
		this._initForcedColors();

		this._addTooltip();

		if (!this._hasLicense()) {
			this._showBranding();
		}
	}

	/**
	 * A shared [[Tooltip]] (theme tag `"system"`) for interface elements such
	 * as the logo and buttons, created on first use.
	 * @since 5.14.0
	 */
	public get systemTooltip() {
		if (!this._systemTooltip) {
			const tooltip = Tooltip.new(this, {
				themeTags: ["system"]
			});

			this._systemTooltip = tooltip;
		}
		return this._systemTooltip;
	}

	private _initResizeSensor(): void {
		if (this._resizeSensorDisposer) {
			this._resizeSensorDisposer.dispose();
		}
		this._resizeSensorDisposer = new ResizeSensor(this.dom, () => {
			if (this.autoResize) {
				this.resize();
			}
		});
		this._disposers.push(this._resizeSensorDisposer);
	}

	/**
	 * Resizes the chart to fit its container. Call it after the container's
	 * size changes when `autoResize` is `false`.
	 */
	public resize(): void {
		const rect = this._getRealSize();
		const size = this._getCalculatedSize(rect);
		const w = Math.floor(size.width);
		const h = Math.floor(size.height);

		if (w > 0 && h > 0) {
			const realWidth = Math.floor(rect.width);
			const realHeight = Math.floor(rect.height);

			const htmlElementContainer = this._htmlElementContainer!;
			htmlElementContainer.style.width = w + "px";
			htmlElementContainer.style.height = h + "px";

			if (this._a11yD !== true) {
				const focusElementContainer = this._focusElementContainer!;
				focusElementContainer.style.width = w + "px";
				focusElementContainer.style.height = h + "px";
			}

			this._renderer.resize(realWidth, realHeight, w, h);

			const rootContainer = this._rootContainer;

			rootContainer.setPrivate("width", w);
			rootContainer.setPrivate("height", h);
			this._render();
			this._handleLogo();
		}
	}

	/**
	 * Re-applies the current `_settings` to the live `Root` and its sub-objects
	 * without tearing anything down.
	 *
	 * Unlike `_init()`, this creates no new containers, DOM elements, or event
	 * listeners. Use it after changing settings on an already-initialized `Root`
	 * (e.g. from a parsed `root` config section).
	 *
	 * NOTE: `accessible` can only be turned off (matching constructor behavior);
	 * it cannot be re-enabled on a live `Root`.
	 *
	 * @ignore
	 */
	public _reapplySettings(): void {
		const settings = this._settings;

		// Renderer resolution (`useSafeResolution`). `getSafeResolution()` returns
		// `undefined` off iOS, which means "use the device pixel ratio".
		const safeResolution = $utils.getSafeResolution();
		this._renderer.resolution = (settings.useSafeResolution && safeResolution != null) ? safeResolution : window.devicePixelRatio;

		// Tooltip container bounds.
		if (settings.tooltipContainerBounds) {
			this._tooltipContainerSettings = settings.tooltipContainerBounds;
		}
		if (this._htmlElementContainer) {
			this._htmlElementContainer.style.overflow = settings.tooltipContainerBounds ? "" : "hidden";
		}

		// Accessibility can only be disabled, never re-enabled on a live `Root`.
		if (settings.accessible === false) {
			this._a11yD = true;
		}

		// Inner element ARIA / focus attributes.
		if (!this._a11yD) {
			const inner = this._inner;

			if (settings.focusable) {
				inner.setAttribute("focusable", "true");
				inner.setAttribute("tabindex", this.tabindex + "");
			}
			else {
				inner.removeAttribute("focusable");
				inner.removeAttribute("tabindex");
			}

			if (settings.ariaLabel != null) {
				inner.setAttribute("aria-label", settings.ariaLabel);
			}
			else {
				inner.removeAttribute("aria-label");
			}

			if (settings.role != null) {
				inner.setAttribute("role", settings.role);
			}
			else {
				inner.removeAttribute("role");
			}
		}

		this._updateForcedColors();

		// `focusPadding`, `sanitizeHTML` and `calculateSize` are read from
		// `_settings` on demand, so they need no explicit re-application.

		// Force every element to re-run its update cycle so values derived from
		// Root settings downstream (e.g. label fonts pulled from `fontFamily`/
		// `fontSize`/`fontWeight`) are re-applied. `markDirty()` on a `Container`
		// re-runs its `_updateChildren()`, which is where those values are pushed.
		if (this._rootContainer) {
			this._rootContainer.markDirty();
			this._rootContainer.walkChildren((child) => {
				child.markDirty();
			});
		}

		// Push the new resolution / calculated size to the renderer and re-render.
		this._render();
	}

	/**
	 * The Root's settings.
	 */
	public get settings(): IRootSettings {
		return this._settings;
	}

	private _render() {
		if (this.skipRenderFrame) {
			this.skipRenderFrame = false;
			return;
		}
		this._renderer.render(this._rootContainer._display);

		if (this._focusElementDirty) {
			this._updateDirtyGroups();
			this._updateCurrentFocus();
			this._focusElementDirty = false;
		}
	}

	private _runTickers(currentTime: number) {
		$array.each(this._tickers, (f) => {
			f(currentTime);
		});
	}

	private _runAnimations(currentTime: number): boolean {
		let running = 0;

		$array.keepIf(this._animations, (animation) => {
			const state = animation._runAnimation(currentTime);

			if (state === AnimationState.Stopped) {
				return false;

			} else if (state === AnimationState.Playing) {
				++running;
				return true;

			} else {
				return true;
			}
		});

		this._isDirtyAnimation = false;

		return running === 0;
	}

	private _runDirties() {
		//console.time("tick");
		let allParents: { [id: number]: IParent } = {};

		while (this._isDirtyParents) {
			// This must be before calling _prepareChildren
			this._isDirtyParents = false;

			$object.entries(this._dirtyParents).forEach(([key, parent]) => {
				delete this._dirtyParents[key];

				if (!parent.isDisposed()) {
					allParents[parent.uid] = parent;
					parent._prepareChildren();
				}
			});
		}

		$object.entries(allParents).forEach(([_key, parent]) => {
			parent._updateChildren();
		});

		const objects: Array<Entity> = [];

		//		console.log("_beforeChanged")
		$object.entries(this._dirty).forEach(([key, entity]) => {
			if (entity.isDisposed()) {
				delete this._dirty[key];

			} else {
				objects.push(entity);
				entity._beforeChanged();
			}
		});

		//		console.log("_changed")
		objects.forEach((entity) => {
			entity._changed();
			delete this._dirty[entity.uid];
			entity._clearDirty();
		});

		this._isDirty = false;

		const depths: { [id: number]: number } = {};
		const bounds: Array<IBounds> = [];

		$object.entries(this._dirtyBounds).forEach(([key, entity]) => {
			delete this._dirtyBounds[key];

			if (!entity.isDisposed()) {
				depths[entity.uid] = entity.depth();
				bounds.push(entity);
			}
		});


		this._positionHTMLElements();

		// High depth -> low depth
		bounds.sort((x, y) => {
			return $order.compare(depths[y.uid], depths[x.uid]);
		});

		//		console.log("_updateBounds")
		bounds.forEach((entity) => {
			entity._updateBounds();
		});

		//		console.log("_updatePosition")
		const dirtyPositions = this._dirtyPositions;

		$object.entries(dirtyPositions).forEach(([key, sprite]) => {
			delete dirtyPositions[key];

			if (!sprite.isDisposed()) {
				sprite._updatePosition();
			}
		});

		//		console.log("_afterChanged")
		objects.forEach((entity) => {
			entity._afterChanged();
		});

		$object.entries(allParents).forEach(([_key, parent]) => {
			parent._childrenPrep = false;
			parent._childrenUpdt = false;
		});
		//console.timeEnd("tick");
	}

	private _renderFrame(currentTime: number): boolean {
		if (this._updateTick) {
			if (this.events.isEnabled("framestarted")) {
				this.events.dispatch("framestarted", {
					type: "framestarted",
					target: this,
					timestamp: currentTime,
				});
			}

			this._checkComputedStyles();
			this._runTickers(currentTime);

			const animationDone = this._runAnimations(currentTime);

			this._runDirties();
			this._render();
			this._renderer.resetImageArray();
			this._positionHTMLElements();

			if (this.events.isEnabled("frameended")) {
				this.events.dispatch("frameended", {
					type: "frameended",
					target: this,
					timestamp: currentTime,
				});
			}

			return this._tickers.length === 0 &&
				animationDone &&
				!this._isDirtyAnimation &&
				!this._isDirty;

		} else {
			return true;
		}
	}

	public _runTicker(currentTime: number, now?: boolean) {
		if (!this.isDisposed()) {
			this.animationTime = currentTime;

			const done = this._renderFrame(currentTime);

			// No more work to do
			if (done) {
				this._ticker = null;
				this.animationTime = null;

			} else {
				if (!this._paused) {
					if (now) {
						this._ticker!
					}
					else {
						rAF(this.fps, this._ticker!);
					}
				}
			}
		}
	}

	public _runTickerNow(timeout: number = 10000) {
		if (!this.isDisposed()) {
			const endTime = performance.now() + timeout;

			for (; ;) {
				const currentTime = performance.now();

				if (currentTime >= endTime) {
					this.animationTime = null;
					break;
				}

				this.animationTime = currentTime;

				const done = this._renderFrame(currentTime);

				if (done) {
					this.animationTime = null;
					break;
				}
			}
		}
	}

	private _startTicker() {
		if (this._ticker === null) {
			this.animationTime = null;

			this._ticker = (currentTime) => {
				this._runTicker(currentTime);
			};

			rAF(this.fps, this._ticker!);
		}
	}

	/**
	 * Whether the chart updates and redraws.
	 */
	public get updateTick(): boolean {
		return this._updateTick;
	}

	/**
	 * Stops the chart from updating and redrawing with `false`, until set back
	 * to `true`.
	 */
	public set updateTick(value: boolean) {
		this._updateTick = value;

		if (value) {
			this._startTicker();
		}
	}

	public _addDirtyEntity(entity: Entity) {
		this._isDirty = true;

		if (this._dirty[entity.uid] === undefined) {
			this._dirty[entity.uid] = entity;
		}

		this._startTicker();
	}

	public _addDirtyParent(parent: IParent) {
		this._isDirty = true;
		this._isDirtyParents = true;

		if (this._dirtyParents[parent.uid] === undefined) {
			this._dirtyParents[parent.uid] = parent;
		}

		this._startTicker();
	}

	public _addDirtyBounds(entity: IBounds) {
		this._isDirty = true;

		if (this._dirtyBounds[entity.uid] === undefined) {
			this._dirtyBounds[entity.uid] = entity;
		}

		this._startTicker();
	}

	public _addDirtyPosition(sprite: Sprite) {
		this._isDirty = true;

		if (this._dirtyPositions[sprite.uid] === undefined) {
			this._dirtyPositions[sprite.uid] = sprite;
		}

		this._startTicker();
	}

	public _addAnimation(animation: IAnimation) {
		this._isDirtyAnimation = true;

		// TODO use numeric id instead
		if (this._animations.indexOf(animation) === -1) {
			this._animations.push(animation);
		}

		this._startTicker();
	}

	public _markDirty() {
		this._isDirty = true;
	}

	public _markDirtyRedraw() {
		this.events.once("frameended", () => {
			this._isDirty = true;
			this._startTicker();
		})
	}

	/**
	 * Calls a function on every frame, which keeps the chart rendering, until
	 * the returned disposer is disposed.
	 *
	 * @param   f  Function, given the frame's time in milliseconds
	 * @return     Disposer that stops the calls
	 */
	public eachFrame(f: (currentTime: number) => void): IDisposer {
		this._tickers.push(f);
		this._startTicker();

		return new Disposer(() => {
			$array.removeFirst(this._tickers, f);
		});
	}

	/**
	 * Makes every element inside a container update and re-measure on the next
	 * frame.
	 *
	 * @param  container  Container; the main `container` if not given
	 */
	public markDirtyGlobal(container?: Container): void {
		if (!container) {
			container = this.container;
		}
		container.walkChildren((child) => {
			if (child instanceof Container) {
				this.markDirtyGlobal(child);
			}
			child.markDirty();
			child.markDirtyBounds();
		});
	}

	/**
	 * Returns the chart's width in pixels: the container's, or the one from
	 * `calculateSize` if set.
	 *
	 * @return Width
	 */
	public width(): number {
		// TODO make this more efficient, maybe just return the renderer's width ?
		return Math.floor(this._getCalculatedSize(this._getRealSize()).width);
	}

	/**
	 * Returns the chart's height in pixels: the container's, or the one from
	 * `calculateSize` if set.
	 *
	 * @return Height
	 */
	public height(): number {
		// TODO make this more efficient, maybe just return the renderer's height ?
		return Math.floor(this._getCalculatedSize(this._getRealSize()).height);
	}

	/**
	 * Disposes the Root and everything in it, and removes the chart from the
	 * page.
	 */
	public dispose(): void {
		if (!this._isDisposed) {
			this._isDisposed = true;

			this._rootContainer.dispose();
			this._renderer.dispose();
			this.horizontalLayout.dispose();
			this.verticalLayout.dispose();
			this.interfaceColors.dispose();

			$array.each(this._disposers, (x) => {
				x.dispose();
			});

			if (this._inner) {
				$utils.removeElement(this._inner);
			}

			$array.remove(registry.rootElements, this);
		}
	}

	/**
	 * Returns `true` if the Root is disposed.
	 *
	 * @return Disposed?
	 */
	public isDisposed(): boolean {
		return this._isDisposed;
	}

	/**
	 * Makes screen readers read out a message.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/accessibility/} for more info
	 * @param  text  Alert text
	 */
	public readerAlert(text: string): void {
		if (this._a11yD !== true) {
			const element = this._readerAlertElement!;
			text = $utils.stripTags(text);
			if (element.innerHTML == text) {
				element.innerHTML = "";
			}
			element.innerHTML = text;
		}
	}

	/**
	 * Sets the chart's themes, replacing any set before.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/concepts/themes/} for more info
	 * @param  themes  A list of themes
	 */
	public setThemes(themes: Array<Theme>): void {
		this._rootContainer.set("themes", themes);

		// otherwise new themes are not applied
		const tooltipContainer = this.tooltipContainer;
		if (tooltipContainer) {
			tooltipContainer._applyThemes();
		}

		// @todo review this
		const interfaceColors = this.interfaceColors;
		if (interfaceColors) {
			interfaceColors._applyThemes();
		}


	}

	protected _addTooltip() {
		if (!this.tooltipContainer) {
			const tooltipContainerSettings = this._tooltipContainerSettings;
			const tooltipContainer = this._rootContainer.children.push(Container.new(this, {
				position: "absolute",
				isMeasured: false,
				width: p100,
				height: p100,
				layer: tooltipContainerSettings ? 35 : 30,
				layerMargin: tooltipContainerSettings ? tooltipContainerSettings : undefined
			}));
			this.tooltipContainer = tooltipContainer;

			const tooltip = Tooltip.new(this, {});
			this.container._setC("tooltip", tooltip);
			tooltip.hide(0);
			this._tooltip = tooltip;
		}
	}

	/**
	 * Accessibility
	 */

	public _registerTabindexOrder(target: Sprite): void {
		if (this._a11yD == true) {
			return;
		}

		if (target.get("focusable") && !target.getPrivate("focusExcluded")) {
			$array.pushOne(this._tabindexes, target);
		}
		else {
			$array.remove(this._tabindexes, target);
		}
		this._invalidateTabindexes();
	}

	public _unregisterTabindexOrder(target: Sprite): void {
		if (this._a11yD == true) {
			return;
		}

		$array.remove(this._tabindexes, target);
		this._invalidateTabindexes();
	}

	public _invalidateTabindexes(): void {
		if (this._a11yD == true) {
			return;
		}

		this._tabindexes.sort((a: Sprite, b: Sprite) => {
			const aindex = a.get("tabindexOrder", 0);
			const bindex = b.get("tabindexOrder", 0);
			if (aindex == bindex) {
				return 0;
			}
			else if (aindex > bindex) {
				return 1;
			}
			else {
				return -1;
			}
		});

		$array.each(this._tabindexes, (item, index) => {
			if (!item.getPrivate("focusElement")) {
				this._makeFocusElement(index, item);
			}
			else {
				this._moveFocusElement(index, item);
			}
			const group = item.get("focusableGroup");
			if (group) {
				this._dirtyGroups[group] = group;
			}
		});

		this._updateDirtyGroups();
	}

	// Whether the element can take focus now: shown, sized and not opted out.
	// `ariaHidden` opts out too: a hidden element must not take focus
	public _canFocus(target: Sprite): boolean {
		return target.get("visible") && target.get("opacity") !== 0 && target.get("role") != "tooltip" && !target.get("ariaHidden") && !target.isHidden() && target.getPrivate("focusable") !== false && this._hasFocusSize(target) ? true : false;
	}

	// Whether the element is a keyboard stop: it can take focus and is in view.
	// Out of view (e.g. a map object panned away) it is still there for screen
	// readers, just not a TAB stop. The focused element stays one until left
	public _canTab(target: Sprite): boolean {
		return this._canFocus(target) && (this._focusedSprite === target || !target.getPrivate("focusOutOfView"));
	}

	protected _invalidateGroup(group: string | number): void {
		this._dirtyGroups[group] = group;
		this._focusElementDirty = true;
	}

	protected _updateDirtyGroups(): void {
		const groups = this._dirtyGroups;
		this._dirtyGroups = {};
		$object.each(groups, (_key, group) => {
			this._updateGroup(group);
		});
	}

	// Gives a group one TAB stop: the member focused last, or the first one
	// that can take focus. The rest are reached with arrow keys
	protected _updateGroup(group: string | number): void {
		const items = this._tabindexes.filter((item) => item.get("focusableGroup") == group && item.getPrivate("focusElement"));
		let stop: Sprite | undefined = this._groupStops[group];
		if (!stop || items.indexOf(stop) == -1 || !this._canTab(stop)) {
			stop = $array.find(items, (item) => this._canTab(item));
		}

		if (stop) {
			this._groupStops[group] = stop;
		}
		else {
			delete this._groupStops[group];
		}

		$array.each(items, (item) => {
			const dom = item.getPrivate("focusElement")!.dom;
			if (this._canTab(item)) {
				dom.setAttribute("tabindex", item === stop ? "" + this.tabindex : "-1");
			}
			else {
				dom.removeAttribute("tabindex");
			}
		});
	}

	public _updateCurrentFocus(): void {
		if (this._a11yD == true) {
			return;
		}

		if (this._focusedSprite) {
			this._decorateFocusElement(this._focusedSprite);
			this._positionFocusElement(this._focusedSprite);
		}
	}

	public _decorateFocusElement(target: Sprite, focusElement?: HTMLDivElement): void {

		if (this._a11yD == true) {
			return;
		}

		// Decorate with proper accessibility attributes
		if (!focusElement) {
			focusElement = target.getPrivate("focusElement")!.dom;
		}

		if (!focusElement) {
			return;
		}

		// An element clicked to switch a state (`toggleKey`), e.g. a pie slice
		// pulling out. A figure cannot tell its state, so a focusable one that
		// toggles is a toggle button to assistive technology
		const toggleKey = target.get("toggleKey");
		const toggles = toggleKey == "active" || toggleKey == "disabled";
		let role = target.get("role");
		if (role == "figure" && toggles && target.get("focusable")) {
			role = "button";
		}

		if (role) {
			focusElement.setAttribute("role", role);
		}
		else {
			focusElement.removeAttribute("role");
		}

		// Expanded / collapsed wins over pressed for the same toggle
		const ariaExpanded = target.get("ariaExpanded");
		if (ariaExpanded != null) {
			focusElement.setAttribute("aria-expanded", ariaExpanded ? "true" : "false");
		}
		else {
			focusElement.removeAttribute("aria-expanded");
		}

		if (role == "button" && toggles && ariaExpanded == null) {
			const pressed = toggleKey == "disabled" ? !target.get("disabled") : !!target.get("active");
			focusElement.setAttribute("aria-pressed", pressed ? "true" : "false");
		}
		else {
			focusElement.removeAttribute("aria-pressed");
		}

		const ariaLabel = target.get("ariaLabel");
		if (ariaLabel) {
			const label = populateString(target, ariaLabel);
			focusElement.setAttribute("aria-label", label);
		}
		else {
			focusElement.removeAttribute("aria-label");
		}

		const ariaLive = target.get("ariaLive");
		if (ariaLive) {
			focusElement.setAttribute("aria-live", ariaLive);
		}
		else {
			focusElement.removeAttribute("aria-live");
		}

		const ariaChecked = target.get("ariaChecked");
		if (ariaChecked != null && role && ["checkbox", "option", "radio", "menuitemcheckbox", "menuitemradio", "treeitem"].indexOf(role) !== -1) {
			focusElement.setAttribute("aria-checked", ariaChecked ? "true" : "false");
		}
		else {
			focusElement.removeAttribute("aria-checked");
		}

		const ariaCurrent = target.get("ariaCurrent");
		if (ariaCurrent != null) {
			focusElement.setAttribute("aria-current", ariaCurrent);
		}
		else {
			focusElement.removeAttribute("aria-current");
		}

		const ariaSelected = target.get("ariaSelected");
		if (ariaSelected != null && role && ["gridcell", "option", "row", "tab", "columnheader", "rowheader", "treeitem"].indexOf(role) !== -1) {
			focusElement.setAttribute("aria-selected", ariaSelected ? "true" : "false");
		}
		else {
			focusElement.removeAttribute("aria-selected");
		}

		if (target.get("ariaHidden")) {
			focusElement.setAttribute("aria-hidden", "true");
		}
		else {
			focusElement.removeAttribute("aria-hidden");
		}

		const ariaOrientation = target.get("ariaOrientation");
		if (ariaOrientation) {
			focusElement.setAttribute("aria-orientation", ariaOrientation);
		}
		else {
			focusElement.removeAttribute("aria-orientation");
		}

		const ariaValueNow = target.get("ariaValueNow");
		if (ariaValueNow) {
			focusElement.setAttribute("aria-valuenow", ariaValueNow);
		}
		else {
			focusElement.removeAttribute("aria-valuenow");
		}

		const ariaValueMin = target.get("ariaValueMin");
		if (ariaValueMin) {
			focusElement.setAttribute("aria-valuemin", ariaValueMin);
		}
		else {
			focusElement.removeAttribute("aria-valuemin");
		}

		const ariaValueMax = target.get("ariaValueMax");
		if (ariaValueMax) {
			focusElement.setAttribute("aria-valuemax", ariaValueMax);
		}
		else {
			focusElement.removeAttribute("aria-valuemax");
		}

		const ariaValueText = target.get("ariaValueText");
		if (ariaValueText) {
			focusElement.setAttribute("aria-valuetext", ariaValueText);
		}
		else {
			focusElement.removeAttribute("aria-valuetext");
		}

		const ariaControls = target.get("ariaControls");
		if (ariaControls) {
			focusElement.setAttribute("aria-controls", ariaControls);
		}
		else {
			focusElement.removeAttribute("aria-controls");
		}

		const group = target.get("focusableGroup");
		if (this._canFocus(target)) {
			if (!this._canTab(target)) {
				// Out of view: readable, not a TAB stop
				focusElement.removeAttribute("tabindex");
			}
			else if (group) {
				// One TAB stop per group: which member it is is decided per group
				focusElement.setAttribute("tabindex", this._groupStops[group] === target ? "" + this.tabindex : "-1");
			}
			else {
				focusElement.setAttribute("tabindex", "" + this.tabindex);
			}
			focusElement.removeAttribute("aria-hidden");
		}
		else {
			focusElement.removeAttribute("tabindex");
			focusElement.setAttribute("aria-hidden", "true");
		}

		if (group) {
			// The group's TAB stop may have to move to or away from this member
			this._invalidateGroup(group);
		}
	}

	// Interface colors replaced while a forced colors mode is on, to put back
	protected _forcedColorsBackup?: { [key: string]: { value: any, user: boolean } };

	protected _initForcedColors(): void {
		const query = getForcedColorsQuery();
		if (query && query.addEventListener) {
			// High Contrast switched on or off, or its theme changed
			this._disposers.push($utils.addEventListener(query, "change", () => {
				systemColors = undefined;
				this._updateForcedColors();
			}));
		}
		this._updateForcedColors();
	}

	/**
	 * Applies or removes system interface colors for a forced colors mode.
	 *
	 * @ignore
	 */
	public _updateForcedColors(): void {
		const query = getForcedColorsQuery();
		const active = query && query.matches && this._settings.forcedColors !== false;
		const ic = this.interfaceColors;

		if (active) {
			const colors = getSystemColors();
			if (!this._forcedColorsBackup) {
				const backup: { [key: string]: { value: any, user: boolean } } = {};
				$object.each(FORCED_COLORS, (key) => {
					backup[key] = { value: ic.get(key), user: ic.isUserSetting(key) && !ic.isComputedSetting(key) };
				});
				this._forcedColorsBackup = backup;
			}
			$object.each(FORCED_COLORS, (key, name) => {
				ic._setC(key, colors[name!]);
			});
		}
		else if (this._forcedColorsBackup) {
			// Back to the colors set in code, or else the themes'
			const backup = this._forcedColorsBackup;
			this._forcedColorsBackup = undefined;
			$object.each(backup, (key, saved) => {
				if (saved.user) {
					ic.set(<any>key, saved.value);
				}
				else {
					ic.remove(<any>key);
				}
			});
			ic._applyThemes();
		}
	}

	// Ends a drag made with arrow keys, as releasing the mouse would
	protected _stopKeyboardDrag(target: Sprite): void {
		const dragPoint = this._keyboardDragPoint || { x: 0, y: 0 };
		const upEvent = this._renderer.getEvent(new MouseEvent("mouseup", {
			clientX: dragPoint.x,
			clientY: dragPoint.y
		}));

		if (target.events.isEnabled("globalpointerup")) {
			target.events.dispatch("globalpointerup", {
				type: "globalpointerup",
				originalEvent: upEvent.event,
				point: upEvent.point,
				simulated: true,
				target: target
			});
		}
		this._keyboardDragPoint = undefined;
	}

	// A zero-sized element can't be focused. An unmeasured one (a pie slice, a
	// map polygon) has no width or height, so its shape is measured instead.
	// The focused element keeps focus even if it loses its size (a polygon
	// turning to the far side of a globe), until focus moves on
	public _hasFocusSize(target: Sprite): boolean {
		if (this._focusedSprite === target || target.height() || target.width()) {
			return true;
		}
		if (!target.get("isMeasured")) {
			const bounds = target._focusBounds();
			return bounds.right > bounds.left || bounds.bottom > bounds.top;
		}
		return false;
	}

	public _makeFocusElement(index: number, target: Sprite): void {

		if (target.getPrivate("focusElement") || this._a11yD == true) {
			return;
		}

		// Init
		const focusElement = document.createElement("div");
		if (target.get("role") != "tooltip") {
			focusElement.tabIndex = this.tabindex;
		}
		focusElement.style.position = "absolute";
		$utils.setInteractive(focusElement, false);

		const disposers: Array<IDisposer> = [];

		target.setPrivate("focusElement", {
			dom: focusElement,
			disposers,
		});

		this._decorateFocusElement(target);

		disposers.push($utils.addEventListener(focusElement, "focus", (ev: FocusEvent) => {
			this._handleFocus(ev);
		}));

		disposers.push($utils.addEventListener(focusElement, "blur", (ev: FocusEvent) => {
			this._handleBlur(ev);
		}));

		this._moveFocusElement(index, target);

	}

	public _removeFocusElement(target: Sprite): void {
		if (this._a11yD == true) {
			return;
		}

		$array.remove(this._tabindexes, target);
		const focusElement = target.getPrivate("focusElement");
		if (focusElement) {
			const container = this._focusElementContainer!;
			container.removeChild(focusElement.dom);
			$array.each(focusElement.disposers, (x) => {
				x.dispose();
			});
			target.removePrivate("focusElement");

			// Its group needs a new TAB stop
			const group = target.get("focusableGroup");
			if (group) {
				if (this._groupStops[group] === target) {
					delete this._groupStops[group];
				}
				this._invalidateGroup(group);
			}
		}
	}

	public _hideFocusElement(target: Sprite): void {
		if (this._a11yD == true) {
			return;
		}

		const focusElement = target.getPrivate("focusElement")!;
		focusElement.dom.style.display = "none";
	}

	protected _moveFocusElement(index: number, target: Sprite): void {
		if (this._a11yD == true) {
			return;
		}

		// Get container
		const container = this._focusElementContainer!;
		const focusElement = target.getPrivate("focusElement")!.dom;

		if (focusElement === this._focusElementContainer!.children[index]) {
			// Nothing to do
			return;
		}

		const next = this._focusElementContainer!.children[index + 1];
		if (next) {
			container.insertBefore(focusElement, next);
		}
		else {
			container.append(focusElement);
		}
	}

	protected _positionFocusElement(target: Sprite): void {
		if (this._a11yD == true || target == undefined) {
			return;
		}
		this._focusElementContainer!.scrollTop = 0;

		const bounds = target._focusBounds();

		let width = bounds.right == bounds.left ? target.width() : bounds.right - bounds.left;
		let height = bounds.top == bounds.bottom ? target.height() : bounds.bottom - bounds.top;

		const padding = this._settings.focusPadding !== undefined ? this._settings.focusPadding : 2;

		let x = bounds.left - padding;
		let y = bounds.top - padding;

		if (width < 0) {
			x += width;
			width = Math.abs(width);
		}

		if (height < 0) {
			y += height;
			height = Math.abs(height);
		}

		const focusElement = target.getPrivate("focusElement")!.dom;
		focusElement.style.top = y + "px";
		focusElement.style.left = x + "px";
		focusElement.style.width = (width + padding * 2) + "px";
		focusElement.style.height = (height + padding * 2) + "px";

	}

	protected _getSpriteByFocusElement(target: any): Sprite | undefined {
		let found: Sprite | undefined;
		$array.eachContinue(this._tabindexes, (item, _index) => {
			if (item.getPrivate("focusElement")!.dom === target) {
				found = item;
				return false;
			}
			return true;
		});
		return found;
	}

	protected _handleFocus(ev: FocusEvent): void {
		if (this._a11yD == true) {
			return;
		}

		// Get element
		//const focused = this._tabindexes[index];
		const focused = this._getSpriteByFocusElement(ev.target);

		if (!focused) {
			return;
		}

		// Jump over hidden elements
		if (!focused.isVisibleDeep()) {
			this._focusNext(<HTMLDivElement>ev.target, this._isShift ? -1 : 1);
			return;
		}

		// Size and position
		this._positionFocusElement(focused);
		//this._decorateFocusElement(focused);

		this._focusedSprite = focused;

		if (focused.events.isEnabled("focus")) {
			focused.events.dispatch("focus", {
				type: "focus",
				originalEvent: ev,
				target: focused
			});
		}

		// The focused member is its group's TAB stop, so TAB and SHIFT+TAB leave
		// the group from here and come back to it
		const group = focused.get("focusableGroup");
		if (group) {
			this._groupStops[group] = focused;
			this._updateGroup(group);
		}
	}

	protected _focusNext(el: HTMLDivElement, direction: 1 | -1, group?: string | number): void {
		if (this._a11yD == true) {
			return;
		}

		const rootNode = this.dom.getRootNode ? this.dom.getRootNode() : null;
		let queryRoot: Document | ShadowRoot = this.dom.ownerDocument || document;

		if (rootNode) {
			if (typeof ShadowRoot !== "undefined" && rootNode instanceof ShadowRoot) {
				queryRoot = rootNode;
			}
			else if (rootNode instanceof Document) {
				queryRoot = rootNode;
			}
		}

		const focusableElements = Array.from(queryRoot.querySelectorAll([
			'a[href]',
			'area[href]',
			'button:not([disabled])',
			'details',
			'input:not([disabled])',
			'iframe:not([disabled])',
			'select:not([disabled])',
			'textarea:not([disabled])',
			'[contentEditable=""]',
			'[contentEditable="true"]',
			'[contentEditable="TRUE"]',
			'[tabindex]:not([tabindex^="-"])',
			//':not([disabled])'
		].join(',')));

		let index = focusableElements.indexOf(el) + direction;

		if (index < 0) {
			index = focusableElements.length - 1;
		}
		else if (index >= focusableElements.length) {
			index = 0;
		}

		const targetElement = (<HTMLDivElement>focusableElements[index]);

		if (group && direction == -1) {
			const target = this._getSpriteByFocusElement(targetElement);
			if (target && target.get("focusableGroup") == group) {
				this._focusNext(targetElement, direction);
				return;
			}
		}

		if (targetElement) {
			targetElement.focus();
		}
	}

	protected _handleBlur(ev: FocusEvent): void {
		if (this._a11yD == true) {
			return;
		}

		const focused = this._focusedSprite;
		if (focused && !focused.isDisposed() && focused.events.isEnabled("blur")) {
			focused.events.dispatch("blur", {
				type: "blur",
				originalEvent: ev,
				target: focused
			});
		}

		this._focusedSprite = undefined;

		// It may have lost its size while focused
		if (focused && !focused.isDisposed() && focused.getPrivate("focusElement")) {
			this._decorateFocusElement(focused);
		}
	}

	/**
	 * @ignore
	 */
	public updateTooltip(target: Text): void {
		if (this._a11yD == true) {
			return;
		}

		const text = $utils.stripFormatTags($utils.stripTags(target._getText()));
		let tooltipElement = target.getPrivate("tooltipElement");
		if (target.get("role") == "tooltip" && text != "") {
			if (!tooltipElement) {
				tooltipElement = this._makeTooltipElement(target);
			}
			if (tooltipElement.innerHTML != text) {
				tooltipElement.innerHTML = text!;
			}
			tooltipElement.setAttribute("aria-hidden", target.isVisibleDeep() ? "false" : "true");
		}
		else if (tooltipElement) {
			tooltipElement.remove();
			target.removePrivate("tooltipElement");
		}
	}

	public _makeTooltipElement(target: Text): HTMLDivElement {
		const container = this._tooltipElementContainer!;
		const tooltipElement = document.createElement("div");
		tooltipElement.style.position = "absolute";
		tooltipElement.style.width = "1px";
		tooltipElement.style.height = "1px";
		tooltipElement.style.overflow = "hidden";
		tooltipElement.style.clip = "rect(1px, 1px, 1px, 1px)";

		$utils.setInteractive(tooltipElement, false);

		this._decorateFocusElement(target, tooltipElement);
		container.append(tooltipElement);
		target.setPrivate("tooltipElement", tooltipElement);

		return tooltipElement;
	}

	public _removeTooltipElement(target: Text): void {
		if (this._a11yD == true) {
			return;
		}
		const tooltipElement = target.getPrivate("tooltipElement");
		if (tooltipElement) {
			const parent = tooltipElement.parentElement;
			if (parent) {
				parent.removeChild(tooltipElement);
			}
		}
	}

	public _invalidateAccessibility(target: Sprite): void {
		if (this._a11yD == true) {
			return;
		}
		this._focusElementDirty = true;
		const focusElement = target.getPrivate("focusElement");
		if (target.get("focusable") && !target.getPrivate("focusExcluded")) {
			if (focusElement) {
				this._decorateFocusElement(target);
				if (this._focusedSprite === target) {
					this._positionFocusElement(target);
				}
			}
			// else {
			// 	this._renderer._makeFocusElement(0, this);
			// }
		}
		else if (focusElement) {
			this._removeFocusElement(target);
		}
		//this.updateCurrentFocus();
	}

	/**
	 * Returns `true` if `target` is currently focused.
	 *
	 * @param   target  Target
	 * @return          Focused?
	 */
	public focused(target: Sprite): boolean {
		return this._focusedSprite === target;
	}

	/**
	 * Converts a point from viewport coordinates, such as a pointer event's
	 * `clientX` and `clientY`, to coordinates within the root.
	 *
	 * @param   point  Viewport point
	 * @return         Root point
	 */
	public documentPointToRoot(point: IPoint): IPoint {
		const rect = this._getRealSize();
		const size = this._getCalculatedSize(rect);

		const scaleWidth = size.width / rect.width;
		const scaleHeight = size.height / rect.height;

		return {
			x: (point.x - rect.left) * scaleWidth,
			y: (point.y - rect.top) * scaleHeight,
		};
	}

	/**
	 * Converts a point from coordinates within the root to viewport
	 * coordinates, as in a pointer event's `clientX` and `clientY`.
	 *
	 * @param   point  Root point
	 * @return         Viewport point
	 */
	public rootPointToDocument(point: IPoint): IPoint {
		const rect = this._getRealSize();
		const size = this._getCalculatedSize(rect);

		const scaleWidth = size.width / rect.width;
		const scaleHeight = size.height / rect.height;

		return {
			x: (point.x / scaleWidth) + rect.left,
			y: (point.y / scaleHeight) + rect.top
		};
	}

	/**
	 * @ignore
	 */
	public addDisposer<T extends IDisposer>(disposer: T): T {
		this._disposers.push(disposer);
		return disposer;
	}

	protected _updateComputedStyles(): boolean {
		// Live: it follows the element's style, so it is read once
		if (!this._styles) {
			this._styles = window.getComputedStyle(this.dom);
		}
		const styles = this._styles;
		const fonts = [styles.fontFamily, styles.fontSize, styles.fontStyle, styles.fontWeight, styles.fontVariant, styles.fontStretch].join();

		if (fonts !== this._fontHash) {
			this._fontHash = fonts;
			return true;
		}
		return false;
	}

	protected _checkComputedStyles(): void {
		if (this._updateComputedStyles()) {
			this._invalidateLabelBounds(this.container);
		}
	}

	protected _invalidateLabelBounds(target: Sprite, remeasure?: boolean): void {
		if (target instanceof Container) {
			target.children.each((child) => {
				this._invalidateLabelBounds(child, remeasure);
			});
		}
		else if (target instanceof Text) {
			target.markDirtyBounds();
			if (remeasure) {
				target._display.invalidateBounds();
				target.markDirty();
			}
		}
	}

	/**
	 * To all the clever heads out there. Yes, we did not make any attempts to
	 * scramble this.
	 *
	 * This is a part of a tool meant for our users to manage their commercial
	 * licenses for removal of amCharts branding from charts.
	 *
	 * The only legit way to do so is to purchase a commercial license for amCharts:
	 * https://www.amcharts.com/online-store/
	 *
	 * Removing or altering this code, or disabling amCharts branding in any other
	 * way is against the license and thus illegal.
	 */
	protected _hasLicense(): boolean {
		for (let i = 0; i < registry.licenses.length; i++) {
			if (registry.licenses[i].match(/^AM5C.{5,}/i)) {
				return true;
			}
		}
		return false;
	}

	public _licenseApplied(): void {
		if (this._logo) {
			this._logo.set("forceHidden", true);
		}
	}

	/**
	 * @ignore
	 */
	public get debugGhostView(): boolean {
		return this._renderer.debugGhostView;
	}

	/**
	 * @ignore
	 */
	public set debugGhostView(value: boolean) {
		this._renderer.debugGhostView = value;
	}

	/**
	 * Makes touch gestures such as zoom and pan work only after a tap on the
	 * chart, so the page can still be scrolled over it.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/getting-started/root-element/#Touch_related_options} for more info
	 * @default false
	 * @since 5.2.9
	 * @param  value  Needs a tap to activate touch functions
	 */
	public set tapToActivate(value: boolean) {
		this._renderer.tapToActivate = value;
	}

	/**
	 * @return Needs a tap to activate touch functions
	 */
	public get tapToActivate(): boolean {
		return this._renderer.tapToActivate;
	}

	/**
	 * How long in milliseconds the chart stays active after the last touch,
	 * with `tapToActivate`, before handing touch gestures back to the page.
	 * `0` keeps it active until a tap outside the chart.
	 *
	 * @see {@link https://www.amcharts.com/docs/v5/getting-started/root-element/#Touch_related_options} for more info
	 * @default 3000
	 * @since 5.2.9
	 * @param  value  Timeout
	 */
	public set tapToActivateTimeout(value: number) {
		this._renderer.tapToActivateTimeout = value;
	}

	/**
	 * @return Timeout
	 */
	public get tapToActivateTimeout(): number {
		return this._renderer.tapToActivateTimeout;
	}

	public _makeHTMLElement(target: Container): HTMLDivElement {

		// Get container
		const container = this._htmlElementContainer!;

		// Init
		const htmlElement = document.createElement("div");
		target.setPrivate("htmlElement", htmlElement);

		// Should we put this into a wrapper?
		let needWrapper: Boolean = false;
		let wrapperTarget: Container;
		target._walkParents((parent) => {
			if ((parent as Container).get("verticalScrollbar")) {
				needWrapper = true;
				wrapperTarget = parent as Container;
				return false;
			}
		});
		let htmlElementWrapper: HTMLDivElement;

		if (needWrapper) {
			htmlElementWrapper = document.createElement("div");
			target.setPrivate("htmlElementWrapper", htmlElementWrapper);
			target.setPrivate("wrapperContainer", wrapperTarget!);

			htmlElementWrapper.style.position = "absolute";
			htmlElementWrapper.style.overflow = "hidden";
			htmlElementWrapper.style.boxSizing = "border-box";
			htmlElementWrapper.style.top = "0px";
			htmlElementWrapper.style.left = "0px";
			htmlElementWrapper.style.width = "100%";
			htmlElementWrapper.style.height = "100%";

			wrapperTarget!.events.on("boundschanged", () => {
				this._positionHTMLElement(target);
			});
		}

		//htmlElement.tabIndex = this.tabindex;
		htmlElement.style.position = "absolute";
		htmlElement.style.overflow = "auto";
		htmlElement.style.boxSizing = "border-box";
		$utils.setInteractive(htmlElement, target.get("interactive", false));

		// Translate events
		if (target.events.isEnabled("click")) {
			$utils.setInteractive(htmlElement, true);
			this._disposers.push($utils.addEventListener<PointerEvent | MouseEvent>(htmlElement, "click", (ev: IPointerEvent) => {
				const downEvent = this._renderer.getEvent(ev);
				target.events.dispatch("click", {
					type: "click",
					originalEvent: downEvent.event,
					point: downEvent.point,
					simulated: false,
					target: target
				});
			}));
		}

		this._positionHTMLElement(target);

		if (needWrapper) {
			htmlElementWrapper!.append(htmlElement);
			container.append(htmlElementWrapper!);
		}
		else {
			container.append(htmlElement);
		}

		$array.pushOne(this._htmlEnabledContainers, target);

		return htmlElement;
	}

	public _positionHTMLElements(): void {
		$array.each(this._htmlEnabledContainers, (target) => {
			this._positionHTMLElement(target);
		});
	}

	public _positionHTMLElement(target: Container): void {
		const htmlElementWrapper = target.getPrivate("htmlElementWrapper");
		if (htmlElementWrapper) {
			const wrapperTarget = target.getPrivate("wrapperContainer");
			if (wrapperTarget) {
				const bounds = wrapperTarget.globalBounds();
				htmlElementWrapper.style.clipPath = "rect(" + bounds.top + "px " + bounds.right + "px " + bounds.bottom + "px " + bounds.left + "px)";
			}
		}

		const htmlElement = target.getPrivate("htmlElement");
		if (htmlElement) {

			// Translate settings
			const visualSettings = [
				"paddingTop",
				"paddingRight",
				"paddingBottom",
				"paddingLeft",
				"minWidth",
				"minHeight",
				"maxWidth",
				"maxHeight"
			];
			$array.each(visualSettings, (setting: any) => {
				const value = target.get(setting);
				if (value) {
					htmlElement.style[setting] = value + "px";
				}
				else {
					htmlElement.style[setting] = "";
				}
			});

			const strtingSettings = [
				"fontFamily",
				"fontSize",
				"fontStyle",
				"fontWeight",
				"fontStyle",
				"fontVariant",
				"textDecoration"
			];
			$array.each(strtingSettings, (setting: any) => {
				const value = target.get(setting);
				if (value) {
					if (setting == "fontSize" && !$type.isString(value)) {
						htmlElement.style[setting] = value + "px";
					}
					else {
						htmlElement.style[setting] = value + "";
					}
				}
				else {
					htmlElement.style[setting] = "";
				}
			});

			// Init and reset scale / rotation
			const scale = target.compositeScale() || 1;
			const rotation = target.compositeRotation() || 0;
			htmlElement.style.transform = "";
			htmlElement.style.transformOrigin = "";

			// Deal with opacity
			const opacity = target.compositeOpacity();
			setTimeout(() => {
				htmlElement.style.opacity = opacity + "";
			}, 10);

			const visible = target.isVisibleDeep();
			if (visible) {
				htmlElement.style.display = "block";
			}

			// Deal with position
			// const bounds = target.globalBounds();
			// htmlElement.style.top = (bounds.top) + "px";
			// htmlElement.style.left = (bounds.left) + "px";
			let pos = {
				x: target.x() + target.get("dx", 0),
				y: target.y() + target.get("dy", 0)
			}
			if (target.parent) {
				pos = target.parent.toGlobal(pos)
			}
			htmlElement.style.top = pos.y + "px";
			htmlElement.style.left = pos.x + "px";

			// Use width/height if those are set
			const width = target.get("width");
			const height = target.get("height");

			let w: number = 0;
			let h: number = 0;

			if (width) {
				w = target.width();
			}

			if (height) {
				h = target.height();
			}


			if (!width || !height) {
				htmlElement.style.position = "fixed";
				htmlElement.style.width = "";
				htmlElement.style.height = "";
				const bbox = htmlElement.getBoundingClientRect();
				htmlElement.style.position = "absolute";
				if (!width) w = bbox.width;
				if (!height) h = bbox.height;

				let lw = w / scale;
				let lh = h / scale;

				let cx = target.get("centerX", 0);
				let cy = target.get("centerY", 0);

				let ll = 0;
				let lr = 0;
				let lt = 0;
				let lb = 0;

				if (cx instanceof Percent) {
					ll = -cx.value * lw;
					lr = (1 - cx.value) * lw;
				}
				else {
					ll = -cx;
					lr = lw - cx;
				}

				if (cy instanceof Percent) {
					lt = -cy.value * lh;
					lb = (1 - cy.value) * lh;
				}
				else {
					lt = -cy;
					lb = lh - cy;
				}

				target._localBounds = { left: ll, right: lr, top: lt, bottom: lb };

				let previousBounds = target._adjustedLocalBounds;
				let newBounds = target._display.getAdjustedBounds(target._localBounds);
				target._adjustedLocalBounds = newBounds;

				// compare each value of the bounds
				if (previousBounds.left !== newBounds.left || previousBounds.right !== newBounds.right || previousBounds.top !== newBounds.top || previousBounds.bottom !== newBounds.bottom) {
					target.markDirtyBounds();
				}
			}

			if (w > 0) {
				htmlElement.style.minWidth = (w) + "px";
			}
			if (h > 0) {
				htmlElement.style.minHeight = (h) + "px";
			}

			// Hide or show
			if (!visible || opacity == 0) {
				htmlElement.style.display = "none";
			}

			// Center position
			const x = target.get("centerX", 0);
			const originX = isPercent(x) ? (x as Percent).percent + "%" : x + "px";
			const y = target.get("centerY", 0);
			const originY = isPercent(y) ? (y as Percent).percent + "%" : y + "px";

			if (x || y) {
				htmlElement.style.transform = "translate(-" + originX + ", -" + originY + ")" + htmlElement.style.transform;
			}

			// Deal with scale
			if (scale != 1) {
				htmlElement.style.transform += "scale(" + scale + ")";
			}

			if (rotation != 0) {
				htmlElement.style.transform += " rotate(" + rotation + "deg)";
			}

			if (htmlElement.style.transform != "") {
				htmlElement.style.transformOrigin = originX + " " + originY;
				//htmlElement.style.transformOrigin = "0% 0%";
			}


		}
	}

	/**
	 * Sanitizes HTML content before it gets injected via `innerHTML`, unless
	 * sanitization is disabled via the `sanitizeHTML` setting.
	 *
	 * @ignore
	 */
	public _sanitizeHTML(html: string): string {
		return this._settings.sanitizeHTML ? $utils.sanitizeHTML(html) : html;
	}

	public _setHTMLContent(target: Container, html: string): void {
		let htmlElement = target.getPrivate("htmlElement");
		if (!htmlElement) {
			htmlElement = this._makeHTMLElement(target);
			if (!this.autoResize) {
				this.resize();
			}
		}
		const sanitized = this._sanitizeHTML(html);
		if (htmlElement.innerHTML != sanitized) {
			htmlElement.innerHTML = sanitized;
		}
	}

	public _removeHTMLContent(target: Container): void {
		const htmlElementWrapper = target.getPrivate("htmlElementWrapper");
		const htmlElement = target.getPrivate("htmlElement");
		if (htmlElementWrapper) {
			this._htmlElementContainer!.removeChild(htmlElementWrapper);
			target.removePrivate("htmlElement");
			target.removePrivate("htmlElementWrapper");
			target.removePrivate("wrapperContainer");
		}
		else if (htmlElement) {
			this._htmlElementContainer!.removeChild(htmlElement);
			target.removePrivate("htmlElement");
			target.removePrivate("wrapperContainer");
		}
		$array.remove(this._htmlEnabledContainers, target);
	}
}
